import { App, Skeleton } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAssistant, usePatchAssistantMutation } from '@entities/assistant';
import {
  answerPrice,
  markModelsSeen,
  ModelAvatar,
  modelMatches,
  quickPicks,
  useModels,
  type LlmModel,
} from '@entities/llm';
import { useUserStore } from '@entities/user';
import { readDraft, writeDraft } from '@features/create-assistant';
import { extractErrorMessage } from '@shared/api';
import { rub, rub0, sec } from '@shared/ui/v2';
import './models.css';

type Mode = 'settings' | 'create';
type View = 'recommended' | 'all';

const PAGE = 30;

function priceText(m: LlmModel): string {
  const p = answerPrice(m);
  if (p.value == null) return '—';
  return (p.approx ? '≈ ' : '') + rub(p.value);
}

/** Цена за 1 млн без лишних «,00»: 376 / 1 880 / 18,8. */
function perM(v: string | number) {
  return new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number(v));
}

/** Первая фраза описания — для строки таблицы. */
function shortDesc(d: string | null): string {
  if (!d) return '';
  const first = d.split(/(?<=[.!?])\s/)[0] ?? d;
  return first.length > 90 ? first.slice(0, 88).trimEnd() + '…' : first.replace(/\.$/, '');
}

const matches = modelMatches;

function Badge({
  kind,
  children,
}: {
  kind: 'default' | 'ru' | 'bad' | 'rec' | 'vision';
  children: React.ReactNode;
}) {
  return <span className={`mp-badge is-${kind}`}>{children}</span>;
}

function SearchBox({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  id: string;
}) {
  return (
    <div className="mp-search">
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#9CA3AF"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="M20 20l-3.5-3.5" />
      </svg>
      <label htmlFor={id} className="v2-sr">
        Поиск модели
      </label>
      <input
        id={id}
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * Экраны 10 «Выбор модели: рекомендованные» и 11 «все модели» (утверждены 28.10.2026).
 * mode=settings — для ассистента (Применить → PATCH model_id), mode=create — для «Нового
 * ассистента» (Применить → модель в черновик и назад на экран 2).
 */
export function ModelPickerPage({ mode, view }: { mode: Mode; view: View }) {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: models, isLoading } = useModels();
  const { data: assistant } = useAssistant(mode === 'settings' ? id : undefined);
  const patch = usePatchAssistantMutation(id ?? '');

  const currentId = useMemo<number | null>(() => {
    if (mode === 'create')
      return readDraft().modelId ?? models?.find((m) => m.is_default)?.id ?? null;
    return assistant?.model_id ?? null;
  }, [mode, assistant, models]);

  const [sel, setSel] = useState<number | null>(null);
  const selectedId = sel ?? currentId;
  const [q, setQ] = useState('');
  const [onlyRu, setOnlyRu] = useState(false);
  const [withOthers, setWithOthers] = useState(false);
  const [vision, setVision] = useState(false);
  const [vendor, setVendor] = useState<string | null>(null);
  const [limit, setLimit] = useState(PAGE);
  const sentinel = useRef<HTMLDivElement>(null);

  const uid = useUserStore((st) => st.user?.id);
  useEffect(() => markModelsSeen(uid), [uid]);

  const base = mode === 'create' ? '/assistants/new' : `/assistants/${id}/settings`;
  const backTo = base;
  // Создание: возвращаемся шагом назад, чтобы «Отмена» на экране 2 вела туда, откуда пришли.
  const backToCreate = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate('/assistants/new', { replace: true });
  };
  const backLabel = mode === 'create' ? 'Новый ассистент' : 'Настройка';
  const all = models ?? [];
  const recommended = all.filter((m) => m.verified);
  const selected = all.find((m) => m.id === selectedId) ?? null;
  const changed = selectedId != null && selectedId !== currentId;
  const picks = quickPicks(all);

  // Подгрузка по мере прокрутки (экран 11).
  useEffect(() => {
    if (view !== 'all' || !sentinel.current) return undefined;
    const io = new IntersectionObserver((e) => {
      if (e[0]?.isIntersecting) setLimit((l) => l + PAGE);
    });
    io.observe(sentinel.current);
    return () => io.disconnect();
    // Пересоздаём наблюдателя, когда меняется список: после поиска/фильтра «хвост» списка новый.
  }, [view, models, q, onlyRu, vision, vendor, limit]);

  const apply = async () => {
    if (!changed || selectedId == null) return;
    if (mode === 'create') {
      writeDraft({ ...readDraft(), modelId: selectedId });
      backToCreate();
      return;
    }
    try {
      await patch.mutateAsync({ model_id: selectedId });
      message.success(`Модель изменена: ${selected?.title ?? ''}`);
      navigate(backTo);
    } catch (e) {
      message.error(extractErrorMessage(e));
    }
  };

  const pickBtn = (m: LlmModel) => {
    const isSel = m.id === selectedId;
    return (
      <button
        type="button"
        className={isSel ? 'mp-pick is-on' : 'mp-pick'}
        aria-pressed={isSel}
        onClick={() => setSel(m.id)}
      >
        {isSel ? 'Выбрана' : 'Выбрать'}
      </button>
    );
  };

  const header = (
    <header className="v2-topbar is-section mp-top">
      <Link
        to={backTo}
        className="mp-back"
        onClick={(e) => {
          if (mode === 'create') {
            e.preventDefault();
            backToCreate();
          }
        }}
      >
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M15 18l-6-6 6-6" />
        </svg>
        {backLabel}
      </Link>
      <span style={{ color: '#D1D5DB' }}>/</span>
      <h1>Выбор модели</h1>
      <span className="v2-topbar-hint">Сменить можно в любой момент — база знаний останется</span>
      <div className="v2-topbar-actions">
        {selected && (
          <div className="mp-current">
            <div className="v2-xs v2-muted">
              {changed
                ? 'Выбрана новая модель'
                : mode === 'create'
                  ? 'Сейчас выбрана'
                  : 'Сейчас у ассистента'}
            </div>
            <div className="mp-current-name">
              {selected.title}
              {view === 'recommended' &&
                answerPrice(selected).value != null &&
                ` · ${priceText(selected)} за ответ`}
            </div>
          </div>
        )}
        <button
          type="button"
          className="v2-btn is-primary"
          disabled={!changed || patch.isPending}
          onClick={apply}
        >
          Применить
        </button>
      </div>
    </header>
  );

  const tabs = (
    <div className="mp-tabs" role="tablist">
      <Link
        replace
        to={base + '/model'}
        role="tab"
        aria-selected={view === 'recommended'}
        className={view === 'recommended' ? 'is-on' : undefined}
      >
        Рекомендованные RiChat · {recommended.length}
      </Link>
      <Link
        replace
        to={base + '/model/all'}
        role="tab"
        aria-selected={view === 'all'}
        className={view === 'all' ? 'is-on' : undefined}
      >
        Все модели · {all.length}
      </Link>
    </div>
  );

  if (isLoading || !models) {
    return (
      <>
        {header}
        <div className="v2-content is-section">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </>
    );
  }

  if (view === 'recommended') {
    const rows = all
      .filter((m) => m.eval_score != null && (m.verified || withOthers))
      .filter((m) => !onlyRu || m.data_in_russia)
      .filter((m) => matches(m, q))
      .sort(
        (a, b) =>
          // Сначала модели Yandex, затем Рег.облака, затем остальные (решение 31.10).
          (a.group ?? 2) - (b.group ?? 2) ||
          Number(b.verified) - Number(a.verified) ||
          (b.eval_score ?? 0) - (a.eval_score ?? 0),
      );
    const evalDate = all
      .map((m) => m.eval_at)
      .filter(Boolean)
      .sort()
      .pop();
    const presets = [
      { tag: 'ВЫГОДНО', m: picks.cheapest },
      { tag: 'ТОЧНЕЕ ВСЕХ', m: picks.accurate },
      { tag: 'ДАННЫЕ В РОССИИ', m: picks.russian },
    ].filter((p): p is { tag: string; m: LlmModel } => !!p.m);
    return (
      <>
        {header}
        <div className="v2-content is-section mp-page">
          <div className="mp-presets">
            <span className="mp-presets-label">Быстрый выбор:</span>
            {presets.map((p) => (
              <button
                key={p.tag}
                type="button"
                className={p.m.id === selectedId ? 'mp-preset is-on' : 'mp-preset'}
                onClick={() => setSel(p.m.id)}
              >
                <span className="mp-tag">{p.tag}</span>
                <b>{p.m.title}</b>
                <span className="v2-muted">
                  {Math.round(p.m.eval_score ?? 0)}% · {priceText(p.m)}
                </span>
              </button>
            ))}
          </div>

          <section
            aria-labelledby="h-table"
            className="v2-card"
            style={{ overflow: 'hidden', borderRadius: 14 }}
          >
            <div className="mp-table-head">
              <h2 id="h-table" className="v2-h2" style={{ marginRight: 4 }}>
                Сравнение моделей
              </h2>
              {tabs}
              <button
                type="button"
                className={!onlyRu ? 'mp-chip is-on' : 'mp-chip'}
                onClick={() => setOnlyRu(false)}
              >
                Все рекомендованные
              </button>
              <button
                type="button"
                className={onlyRu ? 'mp-chip is-on' : 'mp-chip'}
                onClick={() => setOnlyRu(true)}
              >
                Данные в России
              </button>
              <button
                type="button"
                className={withOthers ? 'mp-chip is-on' : 'mp-chip'}
                onClick={() => setWithOthers((v) => !v)}
              >
                + остальные
              </button>
              <div
                style={{ marginLeft: 'auto', display: 'flex', flex: '0 1 240px', minWidth: 170 }}
              >
                <SearchBox id="mp-q" value={q} onChange={setQ} placeholder="Найти модель" />
              </div>
            </div>
            <div className="v2-table-wrap">
              <table className="v2-table mp-table">
                <thead>
                  <tr>
                    <th scope="col">Модель</th>
                    <th scope="col" style={{ width: 190 }}>
                      Верных ответов
                    </th>
                    <th scope="col">Не выдумывает</th>
                    <th scope="col">Скорость</th>
                    <th scope="col">Цена ответа</th>
                    <th scope="col">1000 ответов</th>
                    <th scope="col">
                      <span className="v2-sr">Выбор</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => {
                    const p = answerPrice(m).value;
                    return (
                      <tr key={m.id} className={m.id === selectedId ? 'is-sel' : undefined}>
                        <td className="v2-wrap" style={{ minWidth: 260 }}>
                          <div className="mp-model">
                            <ModelAvatar model={m} />
                            <div style={{ minWidth: 0 }}>
                              <div className="mp-badges">
                                <span style={{ fontWeight: 600 }}>{m.title}</span>
                                {m.is_default && <Badge kind="default">по умолчанию</Badge>}
                                {m.data_in_russia && <Badge kind="ru">данные в России</Badge>}
                                {!m.verified && <Badge kind="bad">не рекомендована</Badge>}
                              </div>
                              <div className="v2-xs v2-muted">
                                {m.vendor_title} · {shortDesc(m.description)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="mp-bar">
                            <div>
                              <div style={{ width: `${m.eval_score ?? 0}%` }} />
                            </div>
                            <span>{Math.round(m.eval_score ?? 0)}%</span>
                          </div>
                        </td>
                        <td style={{ fontWeight: 500 }}>
                          {m.eval_honesty != null ? `${Math.round(m.eval_honesty)}%` : '—'}
                        </td>
                        <td className="mp-dim">{sec(m.eval_seconds)}</td>
                        <td style={{ fontWeight: 600 }}>{priceText(m)}</td>
                        <td className="mp-dim">{p != null ? rub0(p * 1000) : '—'}</td>
                        <td style={{ textAlign: 'right' }}>{pickBtn(m)}</td>
                      </tr>
                    );
                  })}
                  {rows.length === 0 && (
                    <tr>
                      <td
                        colSpan={7}
                        className="v2-muted"
                        style={{ textAlign: 'center', padding: 24 }}
                      >
                        Ничего не нашлось. Попробуйте «Все модели».
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="mp-foot">
              «Верных ответов» и «Не выдумывает» — проверка RiChat
              {evalDate ? ` от ${new Date(evalDate).toLocaleDateString('ru-RU')}` : ''}, оценивал
              независимый судья. Цена за 1 млн токенов и описание модели — во вкладке «Все модели».
            </div>
          </section>
        </div>
      </>
    );
  }

  // ── Экран 11: все модели ───────────────────────────────────────────────
  const vendorCounts = new Map<
    string,
    { code: string; title: string; n: number; model: LlmModel }
  >();
  for (const m of all) {
    if (!m.vendor_code) continue;
    const v = vendorCounts.get(m.vendor_code) ?? {
      code: m.vendor_code,
      title: m.vendor_title ?? m.vendor_code,
      n: 0,
      model: m,
    };
    v.n += 1;
    vendorCounts.set(m.vendor_code, v);
  }
  const vendors = [...vendorCounts.values()].sort((a, b) => b.n - a.n).slice(0, 8);
  const list = all
    .filter((m) => !onlyRu || m.data_in_russia)
    .filter((m) => !vision || m.supports_vision)
    .filter((m) => !vendor || m.vendor_code === vendor)
    .filter((m) => matches(m, q));
  const shown = list.slice(0, limit);

  return (
    <>
      {header}
      <div className="v2-content is-section mp-page">
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
          {tabs}
          <div style={{ flex: '1 1 260px', maxWidth: 420 }}>
            <SearchBox
              id="mp-q2"
              value={q}
              onChange={(v) => {
                setQ(v);
                setLimit(PAGE);
              }}
              placeholder="Название или задача: «код», «картинки»…"
            />
          </div>
        </div>
        <div className="mp-all">
          <aside aria-label="Фильтры" className="v2-card mp-filters">
            <div className="mp-fgroup">
              <div className="mp-fcap">ПОКАЗАТЬ</div>
              <button
                type="button"
                className="mp-check"
                aria-pressed={onlyRu}
                onClick={() => setOnlyRu((v) => !v)}
              >
                <span className={onlyRu ? 'mp-box is-on' : 'mp-box'} />
                Только данные в России
              </button>
              <button
                type="button"
                className="mp-check"
                aria-pressed={vision}
                onClick={() => setVision((v) => !v)}
              >
                <span className={vision ? 'mp-box is-on' : 'mp-box'} />
                Понимают картинки
              </button>
            </div>
            <div className="mp-fgroup" style={{ gap: 4 }}>
              <div className="mp-fcap" style={{ marginBottom: 4 }}>
                РАЗРАБОТЧИК
              </div>
              {vendors.map((v) => (
                <button
                  key={v.code}
                  type="button"
                  aria-pressed={vendor === v.code}
                  className={vendor === v.code ? 'mp-vendor is-on' : 'mp-vendor'}
                  onClick={() => setVendor(vendor === v.code ? null : v.code)}
                >
                  <ModelAvatar model={v.model} size={20} />
                  <span style={{ flex: 1 }}>{v.title}</span>
                  <span className="v2-muted v2-xs">{v.n}</span>
                </button>
              ))}
            </div>
            <div className="v2-xs v2-muted">Остальные разработчики — через поиск</div>
          </aside>
          <section aria-label="Модели" className="v2-card mp-list">
            <div className="mp-list-head">
              <span>
                Показано {Math.min(limit, list.length)} из {list.length}
              </span>
              <span style={{ marginLeft: 'auto' }}>
                Сортировка: <b style={{ color: '#1F2937' }}>сначала рекомендованные</b>
              </span>
            </div>
            {shown.map((m) => (
              <div key={m.id} className={m.id === selectedId ? 'mp-row is-sel' : 'mp-row'}>
                <ModelAvatar model={m} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mp-badges">
                    <span style={{ fontWeight: 600 }}>{m.title}</span>
                    <span className="v2-xs v2-muted">{m.vendor_title}</span>
                    {m.verified && (
                      <Badge kind="rec">
                        Рекомендовано RiChat · {Math.round(m.eval_score ?? 0)}%
                      </Badge>
                    )}
                    {m.data_in_russia && <Badge kind="ru">данные в России</Badge>}
                    {m.supports_vision && <Badge kind="vision">картинки</Badge>}
                  </div>
                  <div className="v2-small" style={{ color: '#374151', marginTop: 2 }}>
                    {m.description}
                  </div>
                </div>
                <div className="mp-price">
                  <div style={{ fontWeight: 600 }}>{priceText(m)} за ответ</div>
                  <div className="v2-xs v2-muted" title="₽ за 1 млн токенов: вход / выход">
                    {perM(m.price_in_rub_per_m)} / {perM(m.price_out_rub_per_m)}
                    {'\u00a0'}₽ за{'\u00a0'}1{'\u00a0'}млн
                  </div>
                </div>
                {pickBtn(m)}
              </div>
            ))}
            {list.length === 0 && (
              <div className="v2-muted" style={{ padding: 24, textAlign: 'center' }}>
                Ничего не нашлось — попробуйте другое слово или уберите фильтры.
              </div>
            )}
            {limit < list.length && (
              <div ref={sentinel} className="mp-more">
                <button type="button" className="v2-btn" onClick={() => setLimit((l) => l + PAGE)}>
                  Показать ещё
                </button>
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
}
